import { useState } from 'react';
import { LessonHeader } from './LessonHeader';
import { primaryBtn, screenShell } from './common';
import { Badge } from './practice/badges';
import { ClozeExercise } from './practice/ClozeExercise';
import { ListeningChoiceExercise } from './practice/ListeningChoiceExercise';
import { MatchPairsExercise } from './practice/MatchPairsExercise';
import { TrueFalseExercise } from './practice/TrueFalseExercise';
import { ErrorState, LoadingState } from '../../components/ScreenState';
import { useAsync } from '../../hooks/useAsync';
import { contentApi } from '../../api/content';
import { mockListeningChoice, mockMatchPairs, mockTrueFalse } from '../../mock/practice';
import type { ExerciseView } from '../../api/content.types';
import type { LessonScreenProps } from './types';

export const EXERCISE_TYPE_LABEL: Record<string, string> = {
  FLASHCARD: 'Thẻ ghi nhớ',
  MULTIPLE_CHOICE: 'Trắc nghiệm',
  TRACE: 'Luyện viết',
  TAP_TO_BUILD: 'Ghép câu',
  LISTENING_CHOICE: 'Nghe hiểu',
  MATCH_PAIRS: 'Nối cặp',
  CLOZE: 'Điền từ',
  TRUE_FALSE: 'Đúng / Sai',
};

/**
 * Màn dùng chung cho mọi lesson mode=PRACTICE, phân nhánh UI theo `exerciseType`:
 * - CLOZE dùng dữ liệu THẬT từ content API, nhưng API không có đáp án đúng tách riêng
 *   nên nhánh này CHỈ TRÌNH BÀY (highlight chỗ trống), KHÔNG CHẤM ĐIỂM.
 * - LISTENING_CHOICE / MATCH_PAIRS / TRUE_FALSE chạy trên fixture (`src/mock/practice.ts`)
 *   vì DB hiện có 0 bản ghi của 3 loại này.
 * - Các loại còn lại (MULTIPLE_CHOICE, TAP_TO_BUILD, ...) giữ UI generic.
 */
function renderBody(exercise: ExerciseView) {
  switch (exercise.exerciseType) {
    case 'CLOZE':
      return <ClozeExercise instruction={exercise.instruction} example={exercise.example} questions={exercise.questions} />;
    case 'LISTENING_CHOICE':
      return <ListeningChoiceExercise data={mockListeningChoice} />;
    case 'MATCH_PAIRS':
      return <MatchPairsExercise data={mockMatchPairs} />;
    case 'TRUE_FALSE':
      return <TrueFalseExercise data={mockTrueFalse} />;
    default:
      return (
        <>
          <div style={{ alignSelf: 'flex-start' }}>
            <Badge tone="info">{EXERCISE_TYPE_LABEL[exercise.exerciseType] ?? exercise.exerciseType}</Badge>
          </div>
          {exercise.instruction && <h2 style={{ fontSize: 19, fontWeight: 600, color: 'var(--ink)', margin: 0 }}>{exercise.instruction}</h2>}
          {exercise.example && (
            <div style={{ background: 'var(--surface-section)', borderRadius: 'var(--radius-card)', padding: 16, fontFamily: 'var(--font-jp)', fontSize: 16, color: 'var(--ink)', lineHeight: 1.7 }}>
              {exercise.example}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {exercise.questions.map((q) => (
              <div key={q.order} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-control)', padding: 16, fontSize: 16, color: 'var(--ink)', lineHeight: 1.6 }}>
                {q.prompt}
              </div>
            ))}
          </div>
        </>
      );
  }
}

export function PracticeScreen({ lessonId, unitName, lessonName, onBack, onNext }: LessonScreenProps) {
  const exercises = useAsync(() => contentApi.listExercises(lessonId), [lessonId]);
  const [index, setIndex] = useState(0);

  if (exercises.loading) return <LoadingState />;
  if (exercises.error || !exercises.data) return <ErrorState message="Không tải được bài luyện tập." />;
  if (exercises.data.length === 0) return <ErrorState message="Bài này chưa có bài tập." />;

  const list = exercises.data;
  const exercise = list[index];

  const next = () => {
    if (index < list.length - 1) setIndex(index + 1);
    else onNext();
  };

  return (
    <div style={screenShell}>
      <LessonHeader onBack={onBack} ctx={{ track: 'practice', unit: unitName, lesson: lessonName, step: index + 1, total: list.length }} />
      <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
        <div key={exercise.id} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {renderBody(exercise)}
        </div>
      </div>
      <div style={{ padding: 20, borderTop: '1px solid var(--border-default)' }}>
        <button onClick={next} style={primaryBtn}>
          {index < list.length - 1 ? 'Tiếp theo' : 'Hoàn thành'}
        </button>
      </div>
    </div>
  );
}
